import { useState, useEffect, useRef, useMemo } from 'react';
import { ShoppingCart, Plus, Trash2, Check, History, ChevronDown, ChevronUp, Package, User, ScanLine, X, PackageOpen, AlertCircle, Copy, Edit2, Eye, Printer, Star, Phone, UserCheck, Box, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { toast } from 'sonner';
import api from '../lib/api';
import { playSuccessSound, playErrorSound } from '../lib/utils';

// Funcție pentru a rosti prețul
const speakPrice = (price) => {
    try {
        if (!window.speechSynthesis) return;
        window.speechSynthesis.cancel();
        const priceText = String(Math.round(price * 100) / 100);
        const utterance = new SpeechSynthesisUtterance(priceText);
        utterance.lang = 'ro-RO';
        utterance.rate = 1.1;
        window.speechSynthesis.speak(utterance);
    } catch (e) {
        console.error('Speech error:', e);
    }
};

export const ComenziVirtuale = () => {
    const [loading, setLoading] = useState(true);
    const [activeOrders, setActiveOrders] = useState([]);
    const [historyOrders, setHistoryOrders] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [expandedOrders, setExpandedOrders] = useState({});
    
    // Virtual reservations from Rezervări
    const [virtualReservations, setVirtualReservations] = useState([]);
    const [showVirtualReservations, setShowVirtualReservations] = useState(true);
    
    // Saved clients (Clienți S)
    const [savedClients, setSavedClients] = useState(() => {
        const saved = localStorage.getItem('savedVirtualClients');
        return saved ? JSON.parse(saved) : [];
    });
    const [showSavedClients, setShowSavedClients] = useState(true);
    
    // New order modal
    const [showNewOrderModal, setShowNewOrderModal] = useState(false);
    const [newClientName, setNewClientName] = useState('');
    const [newOrderNotes, setNewOrderNotes] = useState('');
    
    // Add item modal
    const [showAddItemModal, setShowAddItemModal] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [quantityInput, setQuantityInput] = useState(1);
    const [priceInput, setPriceInput] = useState('');
    const [productLookup, setProductLookup] = useState(null);
    const barcodeInputRef = useRef(null);
    const priceInputRef = useRef(null);
    
    // Scanner
    const [showScanner, setShowScanner] = useState(false);
    
    // Active pallet info
    const [activePallet, setActivePallet] = useState(null);
    
    // Edit item state
    const [editingItem, setEditingItem] = useState(null); // {orderId, itemId}
    const [editQty, setEditQty] = useState(1);
    const [editPrice, setEditPrice] = useState('');

    // View reservation modal state
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingReservation, setViewingReservation] = useState(null);
    const [checkedItems, setCheckedItems] = useState({});
    const [scanInput, setScanInput] = useState('');
    const scanInputRef = useRef(null);

    // Add item to virtual reservation modal state
    const [showAddToReservationModal, setShowAddToReservationModal] = useState(false);
    const [selectedReservationCustomerId, setSelectedReservationCustomerId] = useState(null);
    const [reservationBarcode, setReservationBarcode] = useState('');
    const [reservationProductName, setReservationProductName] = useState('');
    const [reservationQty, setReservationQty] = useState(1);
    const [reservationPrice, setReservationPrice] = useState('');
    const reservationBarcodeRef = useRef(null);
    const reservationPriceRef = useRef(null);

    // Edit client modal state
    const [showEditClientModal, setShowEditClientModal] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [editClientName, setEditClientName] = useState('');
    const [editClientPhone, setEditClientPhone] = useState('');
    const [editClientAddress, setEditClientAddress] = useState('');
    const [savingClient, setSavingClient] = useState(false);

    // Edit reservation item state (for virtual reservations view modal)
    const [editingReservationItem, setEditingReservationItem] = useState(null); // {itemId, name, barcode, quantity, unit_price}
    const [editResQty, setEditResQty] = useState(1);
    const [editResPrice, setEditResPrice] = useState('');

    // Box assignment state
    const [showBoxModal, setShowBoxModal] = useState(false);
    const [boxAssignCustomerId, setBoxAssignCustomerId] = useState(null);
    const [boxInput, setBoxInput] = useState('');

    // Move items to another client state
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [moveTargetCustomerId, setMoveTargetCustomerId] = useState('');

    // Min sold price for reservation modal
    const [resMinSoldPrice, setResMinSoldPrice] = useState(null);
    const [resTimesSold, setResTimesSold] = useState(0);

    // Focus scan input when view modal opens
    useEffect(() => {
        if (showViewModal && scanInputRef.current) {
            setTimeout(() => scanInputRef.current?.focus(), 200);
        }
    }, [showViewModal]);

    // Complete virtual reservation
    const completeVirtualReservation = async (customerId) => {
        if (!window.confirm(`Finalizezi rezervarea pentru ${customerId}? Va fi mutată în istoric.`)) {
            return;
        }
        try {
            const res = await api.post(`/reservations/v2/complete-virtual/${customerId}`);
            // Clear saved checked items from localStorage
            localStorage.removeItem(`checkedItems_${customerId}`);
            toast.success(res.data.message || 'Rezervare finalizată');
            setShowViewModal(false);
            setViewingReservation(null);
            setCheckedItems({});
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la finalizare');
        }
    };

    // Convert virtual client to normal client
    const convertToNormalClient = async (customerId) => {
        const newCustomerId = window.prompt(
            `Introdu noul ID numeric pentru clientul ${customerId}:`,
            ''
        );
        
        if (!newCustomerId || !newCustomerId.trim()) {
            return;
        }
        
        // Validate it's numeric
        if (!/^\d+(-\d+)?$/.test(newCustomerId.trim())) {
            toast.error('ID-ul trebuie să fie numeric (ex: 123 sau 123-1)');
            return;
        }
        
        if (!window.confirm(`Transformi clientul ${customerId} în clientul ${newCustomerId}?\n\nAceastă acțiune va:\n• Verifica și înregistra toate produsele\n• Muta rezervările la noul ID\n• Șterge clientul virtual`)) {
            return;
        }
        
        try {
            const res = await api.post(`/reservations/v2/convert-virtual/${customerId}`, {
                new_customer_id: newCustomerId.trim()
            });
            toast.success(res.data.message || `Client convertit în ${newCustomerId}`);
            setShowViewModal(false);
            setViewingReservation(null);
            setCheckedItems({});
            localStorage.removeItem(`checkedItems_${customerId}`);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la conversie');
        }
    };

    // Assign box to virtual reservation
    const assignBox = async () => {
        if (!boxAssignCustomerId || !boxInput.trim()) {
            toast.error('Introdu numărul cutiei');
            return;
        }
        try {
            await api.post('/order-boxes/assign', {
                customer_id: boxAssignCustomerId,
                box_number: boxInput.trim()
            });
            toast.success(`Cutia ${boxInput.trim()} alocată pentru ${boxAssignCustomerId}`);
            setShowBoxModal(false);
            setBoxInput('');
            setBoxAssignCustomerId(null);
            loadData();
        } catch (err) {
            const errorMsg = err.response?.data?.detail || 'Eroare la alocare cutie';
            toast.error(errorMsg, { duration: 5000 });
            
            // If conflict (409), keep modal open and clear input for retry
            if (err.response?.status === 409) {
                setBoxInput(''); // Clear for new input
                // Modal stays open - user can enter a different box
            }
            // For other errors, also keep modal open
        }
    };

    // Move checked items to another virtual client
    const moveCheckedItems = async () => {
        if (!viewingReservation || !moveTargetCustomerId.trim()) {
            toast.error('Introdu ID-ul clientului destinație');
            return;
        }

        // Get items that are checked
        const itemsToMove = viewingReservation.items.filter(item => {
            const checkedCount = checkedItems[item.id] || 0;
            return checkedCount > 0;
        });

        if (itemsToMove.length === 0) {
            toast.error('Bifează produsele pe care vrei să le muți');
            return;
        }

        const targetId = moveTargetCustomerId.trim().toUpperCase();
        
        if (!window.confirm(`Muți ${itemsToMove.length} produse bifate de la ${viewingReservation.customer_id} la ${targetId}?`)) {
            return;
        }

        try {
            // Move each checked item
            for (const item of itemsToMove) {
                const checkedCount = checkedItems[item.id] || 0;
                
                // If all quantity is checked, move entire item
                if (checkedCount >= item.quantity) {
                    await api.post(`/reservations/v2/move-item`, {
                        source_customer_id: viewingReservation.customer_id,
                        target_customer_id: targetId,
                        item_id: item.id,
                        quantity: item.quantity
                    });
                } else {
                    // Partial move - move only checked quantity
                    await api.post(`/reservations/v2/move-item`, {
                        source_customer_id: viewingReservation.customer_id,
                        target_customer_id: targetId,
                        item_id: item.id,
                        quantity: checkedCount
                    });
                }
            }
            
            toast.success(`${itemsToMove.length} produse mutate la ${targetId}`);
            setShowMoveModal(false);
            setMoveTargetCustomerId('');
            setCheckedItems({});
            localStorage.removeItem(`checkedItems_${viewingReservation.customer_id}`);
            setShowViewModal(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la mutare');
        }
    };

    // Open view modal for virtual reservation
    const openViewReservation = (reservation) => {
        setViewingReservation(reservation);
        // Load saved checked items from localStorage
        const savedKey = `checkedItems_${reservation.customer_id}`;
        const savedChecked = localStorage.getItem(savedKey);
        if (savedChecked) {
            try {
                setCheckedItems(JSON.parse(savedChecked));
            } catch {
                setCheckedItems({});
            }
        } else {
            setCheckedItems({});
        }
        setScanInput('');
        setEditingReservationItem(null);
        setShowViewModal(true);
    };

    // Edit reservation item
    const startEditReservationItem = (item, e) => {
        e.stopPropagation();
        setEditingReservationItem(item);
        setEditResQty(item.quantity || 1);
        setEditResPrice(item.unit_price?.toString() || '');
    };

    const saveReservationItemEdit = async () => {
        if (!editingReservationItem) return;
        
        const newQty = parseInt(editResQty) || 1;
        const newPrice = parseFloat(editResPrice) || 0;
        
        try {
            await api.put(`/reservations/v2/items/${editingReservationItem.id}`, {
                quantity: newQty,
                unit_price: newPrice
            });
            toast.success('Produs actualizat');
            
            // Update local state
            setViewingReservation(prev => ({
                ...prev,
                items: prev.items.map(item => 
                    item.id === editingReservationItem.id 
                        ? { ...item, quantity: newQty, unit_price: newPrice }
                        : item
                ),
                total_value: prev.items.reduce((sum, item) => {
                    if (item.id === editingReservationItem.id) {
                        return sum + (newQty * newPrice);
                    }
                    return sum + ((item.quantity || 1) * (item.unit_price || 0));
                }, 0)
            }));
            
            setEditingReservationItem(null);
            loadData(); // Refresh to sync totals
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la salvare');
        }
    };

    const cancelReservationItemEdit = () => {
        setEditingReservationItem(null);
    };

    // Delete reservation item
    const deleteReservationItem = async (itemId, itemName, e) => {
        e.stopPropagation();
        if (!window.confirm(`Ștergi produsul "${itemName}" din rezervare?`)) return;
        
        try {
            await api.delete(`/reservations/v2/items/${itemId}`);
            toast.success('Produs șters');
            
            // Update local state
            setViewingReservation(prev => {
                const newItems = prev.items.filter(item => item.id !== itemId);
                return {
                    ...prev,
                    items: newItems,
                    total_value: newItems.reduce((sum, item) => sum + ((item.quantity || 1) * (item.unit_price || 0)), 0)
                };
            });
            
            // Remove from checked items
            setCheckedItems(prev => {
                const newChecked = { ...prev };
                delete newChecked[itemId];
                return newChecked;
            });
            
            loadData(); // Refresh
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la ștergere');
        }
    };

    // Open add to reservation modal
    const openAddToReservation = (customerId) => {
        setSelectedReservationCustomerId(customerId);
        setReservationBarcode('');
        setReservationProductName('');
        setReservationQty(1);
        setReservationPrice('');
        setShowAddToReservationModal(true);
        setTimeout(() => reservationBarcodeRef.current?.focus(), 200);
    };

    // Lookup product for reservation
    const lookupReservationProduct = async () => {
        if (!reservationBarcode.trim()) return;
        
        try {
            const res = await api.get(`/products/lookup/${reservationBarcode.trim().toUpperCase()}`);
            if (res.data) {
                setReservationProductName(res.data.product?.name || reservationBarcode);
                setResMinSoldPrice(res.data.min_sold_price || null);
                setResTimesSold(res.data.times_sold || 0);
                setTimeout(() => reservationPriceRef.current?.focus(), 100);
            }
        } catch {
            setReservationProductName(reservationBarcode);
            setResMinSoldPrice(null);
            setResTimesSold(0);
            setTimeout(() => reservationPriceRef.current?.focus(), 100);
        }
    };

    // Add item to virtual reservation
    const addItemToReservation = async () => {
        if (!reservationBarcode.trim()) {
            toast.error('Introdu codul de bare');
            return;
        }
        if (!reservationPrice || reservationPrice === '') {
            toast.error('Introdu prețul');
            return;
        }

        try {
            await api.post('/reservations/v2/add', {
                customer_id: selectedReservationCustomerId,
                barcode: reservationBarcode.trim().toUpperCase(),
                name: reservationProductName || reservationBarcode.trim().toUpperCase(),
                quantity: parseInt(reservationQty) || 1,
                unit_price: parseFloat(reservationPrice),
                source: 'store'
            });
            
            toast.success('Produs adăugat la rezervare');
            setReservationBarcode('');
            setReservationProductName('');
            setReservationQty(1);
            setReservationPrice('');
            loadData();
            setTimeout(() => reservationBarcodeRef.current?.focus(), 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
        }
    };

    // Handle scan to check item
    const handleScanCheck = (barcode) => {
        if (!barcode.trim() || !viewingReservation) return;
        
        const normalizedBarcode = barcode.trim().toUpperCase();
        const items = viewingReservation.items || [];
        
        // Find item by barcode that's not fully checked
        const foundItem = items.find(item => {
            if (item.barcode?.toUpperCase() !== normalizedBarcode) return false;
            const checkedCount = checkedItems[item.id] || 0;
            return checkedCount < (item.quantity || 1);
        });
        
        if (foundItem) {
            playSuccessSound();
            // Rostește prețul produsului
            const price = foundItem.unit_price || foundItem.price || 0;
            if (price > 0) {
                speakPrice(price);
            }
            const currentCount = checkedItems[foundItem.id] || 0;
            setCheckedItems(prev => ({
                ...prev,
                [foundItem.id]: currentCount + 1
            }));
            setScanInput('');
        } else {
            playErrorSound();
            setScanInput('');
        }
        
        scanInputRef.current?.focus();
    };

    // Toggle check for item
    const toggleItemCheck = (itemId, maxQty) => {
        const currentCount = checkedItems[itemId] || 0;
        if (currentCount >= maxQty) {
            // Uncheck
            setCheckedItems(prev => ({
                ...prev,
                [itemId]: 0
            }));
        } else {
            // Check all
            setCheckedItems(prev => ({
                ...prev,
                [itemId]: maxQty
            }));
        }
    };

    // Save checked items to localStorage when they change
    useEffect(() => {
        if (viewingReservation && Object.keys(checkedItems).length > 0) {
            const savedKey = `checkedItems_${viewingReservation.customer_id}`;
            localStorage.setItem(savedKey, JSON.stringify(checkedItems));
        }
    }, [checkedItems, viewingReservation]);

    useEffect(() => {
        loadData();
        loadActivePallet();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [activeRes, historyRes, reservationsRes] = await Promise.all([
                api.get('/virtual-orders/v2'),
                api.get('/virtual-orders/v2/history'),
                api.get('/reservations/v2')
            ]);
            setActiveOrders(activeRes.data || []);
            setHistoryOrders(historyRes.data || []);
            
            // Filter virtual reservations (customer_id with more than 1 letter)
            const allReservations = reservationsRes.data || [];
            const virtualOnly = allReservations.filter(r => {
                const letterCount = (r.customer_id?.match(/[a-zA-Z]/g) || []).length;
                return letterCount > 1;
            });
            setVirtualReservations(virtualOnly);
        } catch (err) {
            console.error('Error loading virtual orders:', err);
            toast.error('Eroare la încărcarea comenzilor');
        }
        setLoading(false);
    };

    const loadActivePallet = async () => {
        try {
            const res = await api.get('/pallets/active');
            setActivePallet(res.data?.pallet || null);
        } catch (err) {
            console.error('Error loading active pallet:', err);
        }
    };

    // Toggle saved client
    const toggleSavedClient = (customerId) => {
        setSavedClients(prev => {
            const newList = prev.includes(customerId) 
                ? prev.filter(c => c !== customerId)
                : [...prev, customerId];
            localStorage.setItem('savedVirtualClients', JSON.stringify(newList));
            return newList;
        });
    };

    // Split virtual reservations into saved and regular
    const { savedReservations, regularReservations } = useMemo(() => {
        const saved = virtualReservations.filter(r => savedClients.includes(r.customer_id));
        const regular = virtualReservations.filter(r => !savedClients.includes(r.customer_id));
        return { savedReservations: saved, regularReservations: regular };
    }, [virtualReservations, savedClients]);

    // Total for saved clients
    const savedClientsTotal = useMemo(() => {
        return savedReservations.reduce((sum, r) => sum + (r.total_value || 0), 0);
    }, [savedReservations]);

    // Copy reservation list to clipboard (with mobile fallback)
    const copyReservationList = async (reservation) => {
        const lines = reservation.items?.map(item => 
            `${item.name} - ${item.quantity} x ${item.unit_price?.toFixed(2)} RON = ${(item.quantity * item.unit_price).toFixed(2)} RON`
        ) || [];
        const total = reservation.total_value?.toFixed(2) || '0.00';
        const text = `${reservation.customer_id}\n${lines.join('\n')}\n\nTOTAL: ${total} RON`;
        
        // Helper function for fallback copy
        const fallbackCopy = () => {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '0';
            textArea.style.top = '0';
            textArea.style.width = '2em';
            textArea.style.height = '2em';
            textArea.style.padding = '0';
            textArea.style.border = 'none';
            textArea.style.outline = 'none';
            textArea.style.boxShadow = 'none';
            textArea.style.background = 'transparent';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            textArea.setSelectionRange(0, text.length); // For iOS
            
            let success = false;
            try {
                success = document.execCommand('copy');
            } catch (err) {
                success = false;
            }
            
            document.body.removeChild(textArea);
            return success;
        };
        
        // Try modern clipboard API first (works on HTTPS)
        if (navigator.clipboard && window.isSecureContext) {
            try {
                await navigator.clipboard.writeText(text);
                toast.success('Lista copiată în clipboard');
                return;
            } catch (err) {
                // Clipboard API failed, try fallback
                if (fallbackCopy()) {
                    toast.success('Lista copiată în clipboard');
                    return;
                }
            }
        } else {
            // Use fallback directly
            if (fallbackCopy()) {
                toast.success('Lista copiată în clipboard');
                return;
            }
        }
        
        // If all else fails
        toast.error('Nu s-a putut copia. Ține apăsat pe text pentru copiere manuală.');
    };

    // Print reservation as A4 page
    const printReservation = (reservation) => {
        const items = reservation.items || [];
        const customerId = reservation.customer_id || 'Client';
        const total = reservation.total_value?.toFixed(2) || '0.00';
        const date = new Date().toLocaleDateString('ro-RO', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        // Build table rows
        let tableRows = '';
        items.forEach((item, idx) => {
            const lineTotal = (item.quantity * item.unit_price).toFixed(2);
            tableRows += `
                <tr>
                    <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: center;">${idx + 1}</td>
                    <td style="padding: 3px 4px; border-bottom: 1px solid #ddd;">${item.name}</td>
                    <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: center;">${item.barcode || '-'}</td>
                    <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: center;">${item.quantity}</td>
                    <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: right;">${item.unit_price?.toFixed(2)}</td>
                    <td style="padding: 3px 4px; border-bottom: 1px solid #ddd; text-align: right; font-weight: 500;">${lineTotal}</td>
                </tr>
            `;
        });

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Comandă - ${customerId}</title>
                <style>
                    @page { size: A4; margin: 10mm; }
                    body {
                        font-family: Arial, sans-serif;
                        font-size: 10pt;
                        line-height: 1.2;
                        color: #333;
                        margin: 0;
                        padding: 10px;
                    }
                    .header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 8px;
                        padding-bottom: 6px;
                        border-bottom: 2px solid #7c3aed;
                    }
                    .header h1 {
                        margin: 0;
                        font-size: 16pt;
                        color: #7c3aed;
                    }
                    .header .client-name {
                        font-size: 12pt;
                        font-weight: bold;
                        color: #7c3aed;
                    }
                    .header .date {
                        color: #666;
                        font-size: 8pt;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-bottom: 10px;
                    }
                    th {
                        background: #7c3aed;
                        color: white;
                        padding: 4px 4px;
                        text-align: left;
                        font-weight: 600;
                        font-size: 9pt;
                    }
                    th:first-child { text-align: center; width: 25px; }
                    th:nth-child(2) { width: auto; }
                    th:nth-child(3) { text-align: center; width: 80px; }
                    th:nth-child(4) { text-align: center; width: 35px; }
                    th:nth-child(5), th:nth-child(6) { text-align: right; width: 50px; }
                    .total-row {
                        background: #f8f4ff;
                        font-size: 11pt;
                    }
                    .total-row td {
                        padding: 8px 4px;
                        font-weight: bold;
                    }
                    .footer {
                        margin-top: 20px;
                        padding-top: 10px;
                        border-top: 1px solid #ddd;
                        display: flex;
                        justify-content: space-between;
                    }
                    .signature {
                        width: 180px;
                        text-align: center;
                    }
                    .signature-line {
                        border-top: 1px solid #333;
                        margin-top: 30px;
                        padding-top: 3px;
                        font-size: 9pt;
                    }
                    @media print {
                        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div>
                        <h1>COMANDĂ</h1>
                        <div class="date">${date}</div>
                    </div>
                    <div class="client-name">${customerId}</div>
                </div>
                
                <table>
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Produs</th>
                            <th>Cod</th>
                            <th>Cant.</th>
                            <th>Preț</th>
                            <th>Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tableRows}
                        <tr class="total-row">
                            <td colspan="5" style="text-align: right;">TOTAL:</td>
                            <td style="text-align: right; color: #7c3aed;">${total} RON</td>
                        </tr>
                    </tbody>
                </table>
                
                <div class="footer">
                    <div class="signature">
                        <div class="signature-line">Semnătură client</div>
                    </div>
                    <div class="signature">
                        <div class="signature-line">Semnătură vânzător</div>
                    </div>
                </div>
            </body>
            </html>
        `;

        // Open print window
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Auto-print after a short delay
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    const createOrder = async () => {
        if (!newClientName.trim()) {
            toast.error('Introdu numele clientului');
            return;
        }
        try {
            await api.post('/virtual-orders/v2', {
                client_name: newClientName.trim(),
                notes: newOrderNotes.trim() || null
            });
            toast.success(`Comandă creată pentru ${newClientName}`);
            setShowNewOrderModal(false);
            setNewClientName('');
            setNewOrderNotes('');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la creare');
        }
    };

    const openAddItemModal = (orderId) => {
        setSelectedOrderId(orderId);
        setBarcodeInput('');
        setQuantityInput(1);
        setPriceInput('');
        setProductLookup(null);
        setShowAddItemModal(true);
        setTimeout(() => barcodeInputRef.current?.focus(), 100);
    };

    const openEditClientModal = (order) => {
        setEditingClient(order);
        setEditClientName(order.client_name || '');
        setEditClientPhone(order.phone || '');
        setEditClientAddress(order.address || '');
        setShowEditClientModal(true);
    };

    const handleSaveClient = async () => {
        if (!editingClient) return;
        setSavingClient(true);
        try {
            await api.put(`/virtual-orders/${editingClient.id}/update-client`, {
                client_name: editClientName.trim(),
                phone: editClientPhone.trim(),
                address: editClientAddress.trim()
            });
            toast.success('Date client salvate!');
            setShowEditClientModal(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la salvarea datelor');
        } finally {
            setSavingClient(false);
        }
    };

    const handleBarcodeLookup = async (barcode) => {
        if (!barcode.trim()) return;
        try {
            const res = await api.get(`/products/lookup/${barcode.trim()}`);
            setProductLookup(res.data);
            if (res.data.found && res.data.product?.price) {
                setPriceInput(res.data.product.price.toString());
            }
            // Move focus to price input after barcode lookup
            setTimeout(() => priceInputRef.current?.focus(), 100);
        } catch (err) {
            setProductLookup({ found: false });
            // Still move focus to price even if product not found
            setTimeout(() => priceInputRef.current?.focus(), 100);
        }
    };

    const handleBarcodeKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleBarcodeLookup(barcodeInput);
        }
    };

    const handleScannedBarcode = (barcode) => {
        setBarcodeInput(barcode);
        handleBarcodeLookup(barcode);
        setShowScanner(false);
        // Focus will be moved to price by handleBarcodeLookup
    };

    const addItemToOrder = async () => {
        if (!barcodeInput.trim()) {
            toast.error('Introdu codul de bare');
            return;
        }
        if (priceInput === '' || priceInput === null || priceInput === undefined) {
            toast.error('Introdu prețul');
            return;
        }
        
        try {
            const res = await api.post(`/virtual-orders/v2/${selectedOrderId}/items`, {
                barcode: barcodeInput.trim(),
                quantity: quantityInput,
                unit_price: parseFloat(priceInput)
            });
            
            let message = res.data.product_name + ' adăugat';
            if (res.data.deducted_from_pallet && res.data.deducted_from_store) {
                message = `${res.data.product_name} adăugat (scăzut de pe palet și magazin)`;
            } else if (res.data.deducted_from_pallet) {
                message = `${res.data.product_name} adăugat (scăzut de pe palet)`;
            }
            toast.success(message);
            
            // Reset for next item
            setBarcodeInput('');
            setQuantityInput(1);
            setPriceInput('');
            setProductLookup(null);
            loadData();
            loadActivePallet();
            
            // Keep modal open for next scan
            setTimeout(() => barcodeInputRef.current?.focus(), 100);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la adăugare');
        }
    };

    const deleteItem = async (orderId, itemId) => {
        try {
            await api.delete(`/virtual-orders/v2/${orderId}/items/${itemId}`);
            toast.success('Produs șters');
            loadData();
        } catch (err) {
            toast.error('Eroare la ștergere');
        }
    };

    const startEditItem = (orderId, item) => {
        setEditingItem({ orderId, itemId: item.id });
        setEditQty(item.quantity);
        setEditPrice(item.unit_price?.toString() || '');
    };

    const cancelEdit = () => {
        setEditingItem(null);
        setEditQty(1);
        setEditPrice('');
    };

    const saveEditItem = async () => {
        if (!editingItem) return;
        if (editPrice === '' || editPrice === null || editPrice === undefined) {
            toast.error('Introdu prețul');
            return;
        }
        if (editQty < 1) {
            toast.error('Cantitatea trebuie să fie cel puțin 1');
            return;
        }
        
        try {
            await api.put(`/virtual-orders/v2/${editingItem.orderId}/items/${editingItem.itemId}`, {
                quantity: editQty,
                unit_price: parseFloat(editPrice)
            });
            toast.success('Produs actualizat');
            cancelEdit();
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la actualizare');
        }
    };

    const completeOrder = async (orderId) => {
        try {
            const res = await api.post(`/virtual-orders/v2/${orderId}/complete`);
            toast.success(`Comandă finalizată - Total: ${res.data.total?.toFixed(2)} RON`);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Eroare la finalizare');
        }
    };

    const deleteOrder = async (orderId) => {
        if (!window.confirm('Sigur vrei să ștergi această comandă?')) return;
        try {
            await api.delete(`/virtual-orders/v2/${orderId}`);
            toast.success('Comandă ștearsă');
            loadData();
        } catch (err) {
            toast.error('Eroare la ștergere');
        }
    };

    const deleteCompletedOrder = async (orderId) => {
        if (!window.confirm('Sigur vrei să ștergi definitiv această comandă finalizată?')) return;
        try {
            await api.delete(`/virtual-orders/v2/${orderId}`);
            toast.success('Comandă ștearsă definitiv');
            loadData();
        } catch (err) {
            toast.error('Eroare la ștergere');
        }
    };

    const copyOrderToClipboard = (order) => {
        const items = order.items || [];
        const total = items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
        
        let text = `📋 Comandă: ${order.client_name}\n`;
        text += `📅 Data: ${formatDate(order.created_at)}\n`;
        if (order.notes) {
            text += `📝 Note: ${order.notes}\n`;
        }
        text += `\n`;
        text += `━━━━━━━━━━━━━━━━━━━━\n`;
        
        items.forEach((item, index) => {
            text += `${index + 1}. ${item.name} (${item.barcode}) - ${item.quantity} x ${item.unit_price?.toFixed(2)} = ${(item.quantity * item.unit_price).toFixed(2)} RON\n`;
        });
        
        text += `━━━━━━━━━━━━━━━━━━━━\n`;
        text += `💰 TOTAL: ${total.toFixed(2)} RON\n`;
        
        navigator.clipboard.writeText(text).then(() => {
            toast.success('Lista copiată în clipboard!');
        }).catch(() => {
            toast.error('Eroare la copiere');
        });
    };

    const toggleOrderExpand = (orderId) => {
        setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        const date = new Date(isoString);
        return date.toLocaleDateString('ro-RO', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="pb-24">
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Comenzi Neconfirmate
                    </h1>
                </div>
                <div className="space-y-3">
                    {[1, 2, 3].map(i => <Skeleton key={i} className="h-24 w-full" />)}
                </div>
            </div>
        );
    }

    return (
        <div className="pb-24" data-testid="comenzi-virtuale-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Comenzi Neconfirmate
                    </h1>
                    <p className="text-zinc-500 mt-1">
                        Liste de comenzi fără înregistrare în stoc
                        {activePallet && (
                            <span className="ml-2 text-amber-600">
                                (Palet #{activePallet.number} activ)
                            </span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => setShowHistory(!showHistory)}
                        variant={showHistory ? "default" : "outline"}
                        className={showHistory ? "bg-violet-500 hover:bg-violet-600" : ""}
                    >
                        <History size={18} className="mr-2" />
                        {showHistory ? 'Ascunde Istoric' : 'Istoric'}
                    </Button>
                    <Button
                        onClick={() => setShowNewOrderModal(true)}
                        className="bg-teal-500 hover:bg-teal-600"
                        data-testid="new-virtual-order-btn"
                    >
                        <Plus size={18} className="mr-2" />
                        Comandă Nouă
                    </Button>
                </div>
            </div>

            {/* Active Pallet Info */}
            {activePallet && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                    <PackageOpen size={18} className="text-amber-600" />
                    <span className="text-sm text-amber-700">
                        Produsele adăugate vor fi scăzute automat de pe <strong>Paletul #{activePallet.number}</strong> (dacă există)
                    </span>
                </div>
            )}

            {!activePallet && (
                <div className="mb-4 p-3 bg-zinc-100 border border-zinc-200 rounded-lg flex items-center gap-2">
                    <AlertCircle size={18} className="text-zinc-500" />
                    <span className="text-sm text-zinc-600">
                        Niciun palet deschis - produsele nu vor fi scăzute din stoc
                    </span>
                </div>
            )}

            {/* Active Orders */}
            {!showHistory && (
                <>
                    <h2 className="text-lg font-semibold text-zinc-800 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Comenzi Active ({activeOrders.length})
                    </h2>
                    
                    {activeOrders.length === 0 ? (
                        <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center">
                            <ShoppingCart size={48} className="mx-auto text-zinc-300 mb-3" />
                            <h3 className="font-medium text-zinc-700">Nicio comandă activă</h3>
                            <p className="text-sm text-zinc-500 mt-1">Creează o comandă nouă pentru a începe</p>
                            <Button
                                onClick={() => setShowNewOrderModal(true)}
                                className="mt-4 bg-teal-500 hover:bg-teal-600"
                            >
                                <Plus size={18} className="mr-2" />
                                Comandă Nouă
                            </Button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {activeOrders.map(order => (
                                <div 
                                    key={order.id} 
                                    className="bg-white border border-teal-200 rounded-lg overflow-hidden"
                                    data-testid={`virtual-order-${order.id}`}
                                >
                                    {/* Order Header */}
                                    <div 
                                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-teal-50"
                                        onClick={() => toggleOrderExpand(order.id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                                                <User size={20} className="text-teal-600" />
                                            </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold text-zinc-900">{order.client_name}</h3>
                                                        {order.phone ? (
                                                            <span className="text-xs text-emerald-600 flex items-center gap-1">
                                                                <Phone size={10} />
                                                                {order.phone}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-amber-600 flex items-center gap-1">
                                                                <AlertCircle size={10} />
                                                                Fără telefon
                                                            </span>
                                                        )}
                                                        <Button
                                                            onClick={(e) => { e.stopPropagation(); openEditClientModal(order); }}
                                                            size="sm"
                                                            variant="ghost"
                                                            className={order.phone ? "text-zinc-400 hover:text-violet-600 h-6 px-1" : "text-amber-600 hover:text-amber-700 h-6 px-1"}
                                                            data-testid={`edit-client-${order.id}`}
                                                        >
                                                            <Edit2 size={12} />
                                                        </Button>
                                                        <Button
                                                            onClick={(e) => { e.stopPropagation(); openAddItemModal(order.id); }}
                                                            size="sm"
                                                            className="bg-teal-500 hover:bg-teal-600 h-7 px-2"
                                                        >
                                                            <Plus size={14} className="mr-1" />
                                                            Adaugă
                                                        </Button>
                                                    </div>
                                                    <p className="text-xs text-zinc-500">
                                                        {order.item_count || 0} produse • {formatDate(order.created_at)}
                                                    </p>
                                                </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                onClick={(e) => { e.stopPropagation(); copyOrderToClipboard(order); }}
                                                size="sm"
                                                variant="ghost"
                                                className="text-blue-600 hover:bg-blue-50 h-8 px-2"
                                                disabled={!order.items?.length}
                                            >
                                                <Copy size={16} />
                                            </Button>
                                            <Button
                                                onClick={(e) => { e.stopPropagation(); deleteOrder(order.id); }}
                                                size="sm"
                                                variant="ghost"
                                                className="text-red-500 hover:bg-red-50 h-8 px-2"
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                            <Button
                                                onClick={(e) => { e.stopPropagation(); completeOrder(order.id); }}
                                                size="sm"
                                                className="bg-emerald-500 hover:bg-emerald-600 h-8"
                                                disabled={!order.items?.length}
                                            >
                                                <Check size={16} className="mr-1" />
                                                Finalizează
                                            </Button>
                                            <span className="text-lg font-bold text-teal-600 ml-2">
                                                {(order.total || 0).toFixed(2)} RON
                                            </span>
                                            {expandedOrders[order.id] ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                        </div>
                                    </div>

                                    {/* Order Items */}
                                    {expandedOrders[order.id] && (
                                        <div className="border-t border-teal-100">
                                            {/* Items list */}
                                            {order.items?.length > 0 ? (
                                                <div className="divide-y divide-zinc-100">
                                                    {order.items.map(item => {
                                                        const isEditing = editingItem?.orderId === order.id && editingItem?.itemId === item.id;
                                                        return (
                                                            <div key={item.id} className="flex items-center justify-between px-4 py-1.5 hover:bg-zinc-50">
                                                                <div className="flex-1 flex items-center gap-2">
                                                                    <span className="font-medium text-zinc-800">{item.name}</span>
                                                                    <span className="text-xs text-zinc-400">({item.barcode})</span>
                                                                    {item.deducted_from_pallet && item.deducted_from_store && (
                                                                        <span className="text-xs text-amber-600">(palet+magazin)</span>
                                                                    )}
                                                                    {item.deducted_from_pallet && !item.deducted_from_store && (
                                                                        <span className="text-xs text-amber-600">(palet)</span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {isEditing ? (
                                                                        <>
                                                                            <Input
                                                                                type="number"
                                                                                min="1"
                                                                                value={editQty}
                                                                                onChange={(e) => setEditQty(parseInt(e.target.value) || 1)}
                                                                                className="w-16 h-7 text-center text-sm"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                            />
                                                                            <span className="text-zinc-400">x</span>
                                                                            <Input
                                                                                type="number"
                                                                                step="0.01"
                                                                                value={editPrice}
                                                                                onChange={(e) => setEditPrice(e.target.value)}
                                                                                className="w-20 h-7 text-right text-sm"
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === 'Enter') {
                                                                                        e.preventDefault();
                                                                                        saveEditItem();
                                                                                    }
                                                                                    if (e.key === 'Escape') {
                                                                                        cancelEdit();
                                                                                    }
                                                                                }}
                                                                            />
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); saveEditItem(); }}
                                                                                className="p-1 text-emerald-600 hover:bg-emerald-100 rounded"
                                                                                title="Salvează"
                                                                            >
                                                                                <Check size={16} />
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); cancelEdit(); }}
                                                                                className="p-1 text-zinc-500 hover:bg-zinc-100 rounded"
                                                                                title="Anulează"
                                                                            >
                                                                                <X size={16} />
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <span className="text-sm text-zinc-600">{item.quantity} x {item.unit_price?.toFixed(2)}</span>
                                                                            <span className={`font-semibold w-20 text-right ${item.unit_price < 0 ? 'text-red-600' : 'text-zinc-900'}`}>
                                                                                {(item.quantity * item.unit_price).toFixed(2)} RON
                                                                            </span>
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); startEditItem(order.id, item); }}
                                                                                className="p-1 text-blue-500 hover:bg-blue-100 rounded"
                                                                                title="Editează"
                                                                            >
                                                                                <Edit2 size={14} />
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => { e.stopPropagation(); deleteItem(order.id, item.id); }}
                                                                                className="p-1 text-red-500 hover:bg-red-100 rounded"
                                                                                title="Șterge"
                                                                            >
                                                                                <X size={16} />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="p-4 text-center text-zinc-500 text-sm">
                                                    Niciun produs adăugat
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Saved Clients (Clienți S) */}
            {!showHistory && savedReservations.length > 0 && (
                <div className="mt-6">
                    <div 
                        className="flex items-center justify-between cursor-pointer mb-3"
                        onClick={() => setShowSavedClients(!showSavedClients)}
                    >
                        <div className="flex items-center gap-3">
                            <h2 className="text-lg font-semibold text-amber-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                <Star size={18} className="inline mr-1 text-amber-500 fill-amber-500" />
                                Clienți S ({savedReservations.length})
                            </h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-bold text-amber-600 text-base sm:text-lg">
                                {savedClientsTotal.toFixed(2)} RON
                            </span>
                            <Button variant="ghost" size="sm">
                                {showSavedClients ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                            </Button>
                        </div>
                    </div>
                    
                    {showSavedClients && (
                        <div className="space-y-3">
                            {savedReservations.map(reservation => (
                                <div 
                                    key={reservation.customer_id} 
                                    className="bg-white border-2 border-amber-300 rounded-lg overflow-hidden"
                                >
                                    {/* Reservation Header - Mobile optimized */}
                                    <div 
                                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 cursor-pointer hover:bg-amber-50"
                                        onClick={() => toggleOrderExpand(`res-${reservation.customer_id}`)}
                                    >
                                        <div className="flex items-center gap-3 mb-2 sm:mb-0">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleSavedClient(reservation.customer_id); }}
                                                className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-full flex items-center justify-center hover:bg-amber-200 transition-colors"
                                            >
                                                <Star size={18} className="text-amber-500 fill-amber-500" />
                                            </button>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-amber-900 truncate">{reservation.customer_id}</h3>
                                                <p className="text-xs text-zinc-500">
                                                    {reservation.items?.length || 0} produse • <span className="font-medium text-amber-600">{reservation.total_value?.toFixed(2)} RON</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                                            <Button
                                                onClick={() => openAddToReservation(reservation.customer_id)}
                                                size="sm"
                                                className="bg-teal-500 hover:bg-teal-600 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Plus size={14} />
                                                <span className="hidden sm:inline ml-1">Adaugă</span>
                                            </Button>
                                            <Button
                                                onClick={() => openViewReservation(reservation)}
                                                size="sm"
                                                variant="outline"
                                                className="border-amber-300 text-amber-600 hover:bg-amber-100 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Eye size={14} />
                                                <span className="hidden sm:inline ml-1">Verifică</span>
                                            </Button>
                                            <Button
                                                onClick={() => completeVirtualReservation(reservation.customer_id)}
                                                size="sm"
                                                className="bg-amber-500 hover:bg-amber-600 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Check size={14} />
                                                <span className="hidden sm:inline ml-1">OK</span>
                                            </Button>
                                            <Button
                                                onClick={() => convertToNormalClient(reservation.customer_id)}
                                                size="sm"
                                                className="bg-emerald-500 hover:bg-emerald-600 h-7 px-2 sm:px-3 text-xs"
                                                title="Transformă în client normal"
                                            >
                                                <UserCheck size={14} />
                                                <span className="hidden sm:inline ml-1">→ Normal</span>
                                            </Button>
                                            <Button
                                                onClick={() => copyReservationList(reservation)}
                                                size="sm"
                                                variant="outline"
                                                className="border-zinc-300 text-zinc-600 hover:bg-zinc-100 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Copy size={14} />
                                            </Button>
                                            <Button
                                                onClick={() => printReservation(reservation)}
                                                size="sm"
                                                variant="outline"
                                                className="border-amber-300 text-amber-600 hover:bg-amber-100 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Printer size={14} />
                                            </Button>
                                            {expandedOrders[`res-${reservation.customer_id}`] 
                                                ? <ChevronUp size={18} className="text-zinc-400 ml-1" />
                                                : <ChevronDown size={18} className="text-zinc-400 ml-1" />
                                            }
                                        </div>
                                    </div>
                                    
                                    {/* Items */}
                                    {expandedOrders[`res-${reservation.customer_id}`] && (
                                        <div className="border-t border-amber-200">
                                            {reservation.items && reservation.items.length > 0 ? (
                                                <div className="divide-y divide-amber-100">
                                                    {reservation.items.map(item => (
                                                        <div 
                                                            key={item.id} 
                                                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-2 bg-amber-50/50"
                                                        >
                                                            <div className="flex items-center gap-2 flex-1 min-w-0 mb-1 sm:mb-0">
                                                                <Package size={14} className="text-amber-400 flex-shrink-0" />
                                                                <span className="font-medium text-zinc-800 truncate text-sm">{item.name}</span>
                                                                <span className="text-xs text-zinc-400">({item.barcode})</span>
                                                            </div>
                                                            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 flex-shrink-0 pl-6 sm:pl-0">
                                                                <span className="text-xs sm:text-sm text-zinc-600">{item.quantity} x {item.unit_price?.toFixed(2)}</span>
                                                                <span className="font-semibold text-sm sm:w-20 text-right text-zinc-900">
                                                                    {(item.quantity * item.unit_price).toFixed(2)} RON
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="p-4 text-center text-zinc-500 text-sm">
                                                    Niciun produs
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Virtual Reservations from Rezervări */}
            {!showHistory && regularReservations.length > 0 && (
                <div className="mt-6">
                    <div 
                        className="flex items-center justify-between cursor-pointer mb-3"
                        onClick={() => setShowVirtualReservations(!showVirtualReservations)}
                    >
                        <h2 className="text-lg font-semibold text-violet-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Rezervări Virtuale ({regularReservations.length} clienți)
                        </h2>
                        <Button variant="ghost" size="sm">
                            {showVirtualReservations ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </Button>
                    </div>
                    
                    {showVirtualReservations && (
                        <div className="space-y-3">
                            {regularReservations.map(reservation => (
                                <div 
                                    key={reservation.customer_id} 
                                    className="bg-white border border-violet-200 rounded-lg overflow-hidden"
                                >
                                    {/* Reservation Header - Mobile optimized */}
                                    <div 
                                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 cursor-pointer hover:bg-violet-50"
                                        onClick={() => toggleOrderExpand(`res-${reservation.customer_id}`)}
                                    >
                                        <div className="flex items-center gap-3 mb-2 sm:mb-0">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); toggleSavedClient(reservation.customer_id); }}
                                                className="w-8 h-8 sm:w-10 sm:h-10 bg-violet-100 rounded-full flex items-center justify-center hover:bg-amber-100 transition-colors"
                                                title="Adaugă la Clienți S"
                                            >
                                                <Star size={18} className="text-violet-400" />
                                            </button>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    {/* Box badge - clickable to edit */}
                                                    {reservation.box_number ? (
                                                        <button
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                setBoxAssignCustomerId(reservation.customer_id);
                                                                setBoxInput(reservation.box_number);
                                                                setShowBoxModal(true);
                                                            }}
                                                            className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold hover:bg-blue-600 cursor-pointer"
                                                            title="Editează cutia"
                                                            data-testid={`edit-box-btn-${reservation.customer_id}`}
                                                        >
                                                            {reservation.box_number}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                setBoxAssignCustomerId(reservation.customer_id);
                                                                setBoxInput('');
                                                                setShowBoxModal(true);
                                                            }}
                                                            className="bg-zinc-200 text-zinc-500 px-2 py-0.5 rounded text-xs font-bold hover:bg-blue-100 hover:text-blue-600"
                                                            title="Alocă cutie"
                                                            data-testid={`assign-box-btn-${reservation.customer_id}`}
                                                        >
                                                            <Box size={12} className="inline mr-1" />
                                                            +
                                                        </button>
                                                    )}
                                                    <h3 className="font-semibold text-violet-900 truncate">{reservation.customer_id}</h3>
                                                    <span className="text-[10px] px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded font-medium hidden sm:inline">
                                                        Din Rezervări
                                                    </span>
                                                </div>
                                                <p className="text-xs text-zinc-500">
                                                    {reservation.items?.length || 0} produse • <span className="font-medium text-violet-600">{reservation.total_value?.toFixed(2)} RON</span>
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 sm:gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
                                            <Button
                                                onClick={() => openAddToReservation(reservation.customer_id)}
                                                size="sm"
                                                className="bg-teal-500 hover:bg-teal-600 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Plus size={14} />
                                                <span className="hidden sm:inline ml-1">Adaugă</span>
                                            </Button>
                                            <Button
                                                onClick={() => openViewReservation(reservation)}
                                                size="sm"
                                                variant="outline"
                                                className="border-violet-300 text-violet-600 hover:bg-violet-100 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Eye size={14} />
                                                <span className="hidden sm:inline ml-1">Verifică</span>
                                            </Button>
                                            <Button
                                                onClick={() => completeVirtualReservation(reservation.customer_id)}
                                                size="sm"
                                                className="bg-violet-500 hover:bg-violet-600 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Check size={14} />
                                                <span className="hidden sm:inline ml-1">OK</span>
                                            </Button>
                                            <Button
                                                onClick={() => convertToNormalClient(reservation.customer_id)}
                                                size="sm"
                                                className="bg-emerald-500 hover:bg-emerald-600 h-7 px-2 sm:px-3 text-xs"
                                                title="Transformă în client normal"
                                            >
                                                <UserCheck size={14} />
                                                <span className="hidden sm:inline ml-1">→ Normal</span>
                                            </Button>
                                            <Button
                                                onClick={() => copyReservationList(reservation)}
                                                size="sm"
                                                variant="outline"
                                                className="border-zinc-300 text-zinc-600 hover:bg-zinc-100 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Copy size={14} />
                                            </Button>
                                            <Button
                                                onClick={() => printReservation(reservation)}
                                                size="sm"
                                                variant="outline"
                                                className="border-violet-300 text-violet-600 hover:bg-violet-100 h-7 px-2 sm:px-3 text-xs"
                                            >
                                                <Printer size={14} />
                                            </Button>
                                            {expandedOrders[`res-${reservation.customer_id}`] 
                                                ? <ChevronUp size={18} className="text-zinc-400 ml-1" />
                                                : <ChevronDown size={18} className="text-zinc-400 ml-1" />
                                            }
                                        </div>
                                    </div>
                                    
                                    {/* Items - Mobile optimized */}
                                    {expandedOrders[`res-${reservation.customer_id}`] && (
                                        <div className="border-t border-violet-100">
                                            {reservation.items && reservation.items.length > 0 ? (
                                                <div className="divide-y divide-violet-50">
                                                    {reservation.items.map(item => (
                                                        <div 
                                                            key={item.id} 
                                                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-2 bg-violet-50/50"
                                                        >
                                                            <div className="flex items-center gap-2 flex-1 min-w-0 mb-1 sm:mb-0">
                                                                <Package size={14} className="text-violet-400 flex-shrink-0" />
                                                                <span className="font-medium text-zinc-800 truncate text-sm">{item.name}</span>
                                                                <span className="text-xs text-zinc-400">({item.barcode})</span>
                                                            </div>
                                                            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3 flex-shrink-0 pl-6 sm:pl-0">
                                                                <span className="text-xs sm:text-sm text-zinc-600">{item.quantity} x {item.unit_price?.toFixed(2)}</span>
                                                                <span className="font-semibold text-sm sm:w-20 text-right text-zinc-900">
                                                                    {(item.quantity * item.unit_price).toFixed(2)} RON
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="p-4 text-center text-zinc-500 text-sm">
                                                    Niciun produs
                                                </div>
                                            )}
                                            <div className="px-4 py-2 bg-violet-100 text-xs text-violet-600 text-center">
                                                Gestionează această rezervare din pagina <a href="/rezervari" className="font-medium underline">Rezervări</a>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* History */}
            {showHistory && (
                <>
                    <h2 className="text-lg font-semibold text-zinc-800 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Istoric Comenzi Neconfirmate ({historyOrders.length})
                    </h2>
                    
                    {historyOrders.length === 0 ? (
                        <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center">
                            <History size={48} className="mx-auto text-zinc-300 mb-3" />
                            <h3 className="font-medium text-zinc-700">Nicio comandă în istoric</h3>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {historyOrders.map(order => (
                                <div 
                                    key={order.id} 
                                    className="bg-white border border-zinc-200 rounded-lg overflow-hidden"
                                >
                                    <div 
                                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-zinc-50"
                                        onClick={() => toggleOrderExpand(order.id)}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                                                <User size={16} className="text-violet-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-zinc-800">{order.client_name}</h3>
                                                <p className="text-xs text-zinc-500">
                                                    {order.item_count || order.items?.length || 0} produse • {formatDate(order.completed_at)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); copyOrderToClipboard(order); }}
                                                className="p-1.5 text-blue-500 hover:bg-blue-100 rounded"
                                                title="Copiază lista"
                                            >
                                                <Copy size={16} />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); deleteCompletedOrder(order.id); }}
                                                className="p-1.5 text-red-500 hover:bg-red-100 rounded"
                                                title="Șterge definitiv"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            <span className="font-bold text-violet-600">
                                                {(order.total || 0).toFixed(2)} RON
                                            </span>
                                            {expandedOrders[order.id] ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                                        </div>
                                    </div>

                                    {expandedOrders[order.id] && order.items?.length > 0 && (
                                        <div className="border-t border-zinc-100 bg-zinc-50">
                                            {order.items.map(item => (
                                                <div key={item.id} className="flex items-center justify-between px-4 py-2 text-sm">
                                                    <span className="text-zinc-700">{item.name}</span>
                                                    <span className="text-zinc-600">
                                                        {item.quantity} x {item.unit_price?.toFixed(2)} = <strong>{(item.quantity * item.unit_price).toFixed(2)} RON</strong>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* New Order Modal */}
            <Dialog open={showNewOrderModal} onOpenChange={setShowNewOrderModal}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Comandă Virtuală Nouă
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>Nume Client *</Label>
                            <Input
                                value={newClientName}
                                onChange={(e) => setNewClientName(e.target.value)}
                                placeholder="Ex: Ion Popescu"
                                className="mt-1"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label>Note (opțional)</Label>
                            <Input
                                value={newOrderNotes}
                                onChange={(e) => setNewOrderNotes(e.target.value)}
                                placeholder="Ex: Livrare vineri"
                                className="mt-1"
                            />
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setShowNewOrderModal(false)} className="flex-1">
                                Anulează
                            </Button>
                            <Button onClick={createOrder} className="flex-1 bg-teal-500 hover:bg-teal-600">
                                Creează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Client Modal */}
            <Dialog open={showEditClientModal} onOpenChange={setShowEditClientModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Editează Date Client
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>Nume Client</Label>
                            <Input
                                value={editClientName}
                                onChange={(e) => setEditClientName(e.target.value)}
                                placeholder="Numele clientului"
                            />
                        </div>
                        <div>
                            <Label className="flex items-center gap-2">
                                <Phone size={14} />
                                Telefon
                                {!editClientPhone && (
                                    <span className="text-xs text-amber-600">(necesar pentru portal)</span>
                                )}
                            </Label>
                            <Input
                                value={editClientPhone}
                                onChange={(e) => setEditClientPhone(e.target.value)}
                                placeholder="07XX XXX XXX"
                                className={!editClientPhone ? "border-amber-300 focus:border-amber-500" : ""}
                            />
                        </div>
                        <div>
                            <Label>Adresă (opțional)</Label>
                            <Input
                                value={editClientAddress}
                                onChange={(e) => setEditClientAddress(e.target.value)}
                                placeholder="Adresa clientului"
                            />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setShowEditClientModal(false)}
                            >
                                Anulează
                            </Button>
                            <Button
                                onClick={handleSaveClient}
                                disabled={savingClient || !editClientName.trim()}
                                className="bg-violet-600 hover:bg-violet-700"
                            >
                                {savingClient ? 'Se salvează...' : 'Salvează'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Item Modal */}
            <Dialog open={showAddItemModal} onOpenChange={setShowAddItemModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            Adaugă Produs
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        {/* Barcode Input */}
                        <div>
                            <Label>Cod de bare</Label>
                            <div className="flex gap-2 mt-1">
                                <Input
                                    ref={barcodeInputRef}
                                    value={barcodeInput}
                                    onChange={(e) => setBarcodeInput(e.target.value)}
                                    onKeyDown={handleBarcodeKeyDown}
                                    placeholder="Scanează sau tastează..."
                                    className="flex-1 font-mono"
                                />
                                <Button
                                    type="button"
                                    onClick={() => setShowScanner(true)}
                                    className="bg-rose-500 hover:bg-rose-600"
                                >
                                    <ScanLine size={18} />
                                </Button>
                                <Button
                                    type="button"
                                    onClick={() => handleBarcodeLookup(barcodeInput)}
                                    variant="outline"
                                >
                                    Caută
                                </Button>
                            </div>
                        </div>

                        {/* Product Lookup Result */}
                        {productLookup && (
                            <div className={`p-3 rounded-lg border ${productLookup.found ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                                {productLookup.found ? (
                                    <>
                                        <p className="font-medium text-emerald-800">{productLookup.product?.name}</p>
                                        <p className="text-sm text-emerald-600">
                                            Preț: {productLookup.product?.price?.toFixed(2) || 0} RON
                                            {productLookup.product?.category && ` • ${productLookup.product.category}`}
                                        </p>
                                    </>
                                ) : (
                                    <p className="text-amber-700">Produs negăsit în baza de date - introdu manual datele</p>
                                )}
                            </div>
                        )}

                        {/* Quantity & Price */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={quantityInput}
                                    onChange={(e) => setQuantityInput(parseInt(e.target.value) || 1)}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Preț unitar (RON) *</Label>
                                <Input
                                    ref={priceInputRef}
                                    type="number"
                                    step="0.01"
                                    value={priceInput}
                                    onChange={(e) => setPriceInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && barcodeInput && priceInput) {
                                            e.preventDefault();
                                            addItemToOrder();
                                        }
                                    }}
                                    placeholder="0.00"
                                    className="mt-1"
                                />
                            </div>
                        </div>

                        {/* Total Preview */}
                        {priceInput && (
                            <div className="p-2 bg-zinc-100 rounded text-center">
                                <span className="text-sm text-zinc-600">Total: </span>
                                <span className="font-bold text-zinc-900">
                                    {(quantityInput * parseFloat(priceInput || 0)).toFixed(2)} RON
                                </span>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setShowAddItemModal(false)} className="flex-1">
                                Închide
                            </Button>
                            <Button 
                                onClick={addItemToOrder} 
                                className="flex-1 bg-teal-500 hover:bg-teal-600"
                                disabled={!barcodeInput || !priceInput}
                            >
                                <Plus size={16} className="mr-1" />
                                Adaugă
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={showScanner}
                onClose={() => setShowScanner(false)}
                onScan={handleScannedBarcode}
            />

            {/* View/Verify Virtual Reservation Modal */}
            <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-violet-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <User size={20} />
                            Verificare Rezervare - {viewingReservation?.customer_id}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="mt-4 space-y-4">
                        {/* Scan to check input */}
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <ScanLine size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                                <Input
                                    ref={scanInputRef}
                                    value={scanInput}
                                    onChange={(e) => setScanInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleScanCheck(scanInput);
                                        }
                                    }}
                                    placeholder="Scanează cod de bare pentru a bifa..."
                                    className="pl-9 h-10"
                                    autoFocus
                                />
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    // Check all items
                                    const allChecked = {};
                                    viewingReservation?.items?.forEach(item => {
                                        allChecked[item.id] = item.quantity || 1;
                                    });
                                    setCheckedItems(allChecked);
                                }}
                                className="h-10 px-3"
                            >
                                <Check size={16} className="mr-1" />
                                Bifează Tot
                            </Button>
                        </div>
                        
                        {/* Progress */}
                        {viewingReservation?.items && (
                            <div className="flex items-center gap-2 text-sm">
                                <span className="text-zinc-500">Progres:</span>
                                <span className="font-medium text-violet-600">
                                    {Object.values(checkedItems).reduce((a, b) => a + b, 0)} / {viewingReservation.items.reduce((sum, item) => sum + (item.quantity || 1), 0)} produse bifate
                                </span>
                            </div>
                        )}
                        
                        {/* Items list */}
                        <div className="max-h-[50vh] overflow-y-auto border border-zinc-200 rounded-lg divide-y divide-zinc-100">
                            {viewingReservation?.items?.map(item => {
                                const checkedCount = checkedItems[item.id] || 0;
                                const isFullyChecked = checkedCount >= (item.quantity || 1);
                                const isEditing = editingReservationItem?.id === item.id;
                                
                                return (
                                    <div 
                                        key={item.id}
                                        className={`px-4 py-3 transition-colors ${
                                            isFullyChecked 
                                                ? 'bg-emerald-50' 
                                                : 'bg-white'
                                        }`}
                                    >
                                        {isEditing ? (
                                            /* Edit mode */
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-zinc-800 truncate">{item.name}</div>
                                                    <div className="text-xs text-zinc-400">{item.barcode}</div>
                                                </div>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    value={editResQty}
                                                    onChange={(e) => setEditResQty(e.target.value)}
                                                    className="w-16 h-8 text-center"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <span className="text-zinc-400">x</span>
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={editResPrice}
                                                    onChange={(e) => setEditResPrice(e.target.value)}
                                                    className="w-20 h-8 text-right"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <Button
                                                    size="sm"
                                                    onClick={(e) => { e.stopPropagation(); saveReservationItemEdit(); }}
                                                    className="h-8 px-2 bg-emerald-600 hover:bg-emerald-700"
                                                >
                                                    <Check size={14} />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => { e.stopPropagation(); cancelReservationItemEdit(); }}
                                                    className="h-8 px-2"
                                                >
                                                    <X size={14} />
                                                </Button>
                                            </div>
                                        ) : (
                                            /* View mode */
                                            <div 
                                                onClick={() => toggleItemCheck(item.id, item.quantity || 1)}
                                                className="flex items-center justify-between cursor-pointer"
                                            >
                                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                                    {/* Checkbox */}
                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                                        isFullyChecked 
                                                            ? 'bg-emerald-500 border-emerald-500 text-white' 
                                                            : checkedCount > 0
                                                                ? 'bg-amber-100 border-amber-400 text-amber-600'
                                                                : 'border-zinc-300'
                                                    }`}>
                                                        {isFullyChecked && <Check size={14} />}
                                                        {!isFullyChecked && checkedCount > 0 && (
                                                            <span className="text-xs font-bold">{checkedCount}</span>
                                                        )}
                                                    </div>
                                                    
                                                    <div className="min-w-0">
                                                        <div className={`font-medium truncate ${isFullyChecked ? 'text-emerald-700 line-through' : 'text-zinc-800'}`}>
                                                            {item.name}
                                                        </div>
                                                        <div className="text-xs text-zinc-400">{item.barcode}</div>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 flex-shrink-0">
                                                    <span className={`text-sm ${isFullyChecked ? 'text-emerald-600' : 'text-zinc-600'}`}>
                                                        {checkedCount}/{item.quantity || 1} x {item.unit_price?.toFixed(2)}
                                                    </span>
                                                    <span className={`font-semibold w-16 text-right ${isFullyChecked ? 'text-emerald-600' : 'text-zinc-900'}`}>
                                                        {((item.quantity || 1) * (item.unit_price || 0)).toFixed(2)}
                                                    </span>
                                                    
                                                    {/* Edit button */}
                                                    <button
                                                        onClick={(e) => startEditReservationItem(item, e)}
                                                        className="p-1.5 rounded hover:bg-blue-100 text-blue-600"
                                                        title="Editează"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>
                                                    
                                                    {/* Delete button */}
                                                    <button
                                                        onClick={(e) => deleteReservationItem(item.id, item.name, e)}
                                                        className="p-1.5 rounded hover:bg-red-100 text-red-600"
                                                        title="Șterge"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Total */}
                        <div className="flex items-center justify-between p-3 bg-violet-50 rounded-lg">
                            <span className="font-medium text-violet-700">Total comandă:</span>
                            <span className="text-xl font-bold text-violet-700">
                                {viewingReservation?.total_value?.toFixed(2)} RON
                            </span>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 flex-wrap">
                            <Button 
                                variant="outline" 
                                onClick={() => setShowViewModal(false)} 
                                className="flex-1"
                            >
                                Închide
                            </Button>
                            {/* Move to another client button - only show if items are checked */}
                            {Object.values(checkedItems).some(v => v > 0) && (
                                <Button 
                                    onClick={() => {
                                        setMoveTargetCustomerId('');
                                        setShowMoveModal(true);
                                    }}
                                    variant="outline"
                                    className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-100"
                                >
                                    <ArrowRight size={16} className="mr-1" />
                                    Mută Bifate
                                </Button>
                            )}
                            <Button 
                                onClick={() => copyReservationList(viewingReservation)} 
                                variant="outline"
                                className="flex-1"
                            >
                                <Copy size={16} className="mr-1" />
                                Copiază
                            </Button>
                            <Button 
                                onClick={() => printReservation(viewingReservation)} 
                                variant="outline"
                                className="flex-1 border-violet-300 text-violet-600 hover:bg-violet-100"
                            >
                                <Printer size={16} className="mr-1" />
                                Print
                            </Button>
                            <Button 
                                onClick={() => {
                                    setShowViewModal(false);
                                    completeVirtualReservation(viewingReservation?.customer_id);
                                }}
                                className="flex-1 bg-violet-500 hover:bg-violet-600"
                            >
                                <Check size={16} className="mr-1" />
                                Finalizează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Item to Virtual Reservation Modal */}
            <Dialog open={showAddToReservationModal} onOpenChange={setShowAddToReservationModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Plus size={18} className="text-teal-500" />
                            Adaugă Produs - {selectedReservationCustomerId}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        <div>
                            <Label>Cod de bare</Label>
                            <div className="flex gap-2 mt-1">
                                <Input
                                    ref={reservationBarcodeRef}
                                    value={reservationBarcode}
                                    onChange={(e) => setReservationBarcode(e.target.value.toUpperCase())}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            lookupReservationProduct();
                                        }
                                    }}
                                    placeholder="Scanează sau tastează..."
                                />
                                <Button onClick={lookupReservationProduct} variant="outline">
                                    Caută
                                </Button>
                            </div>
                        </div>

                        {reservationProductName && (
                            <div className="p-2 bg-zinc-100 rounded text-sm">
                                <span className="font-medium">{reservationProductName}</span>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Cantitate</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={reservationQty}
                                    onChange={(e) => setReservationQty(e.target.value)}
                                    className="mt-1"
                                />
                            </div>
                            <div>
                                <Label>Preț unitar (RON)</Label>
                                <Input
                                    ref={reservationPriceRef}
                                    type="number"
                                    step="0.01"
                                    value={reservationPrice}
                                    onChange={(e) => setReservationPrice(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && reservationBarcode && reservationPrice) {
                                            e.preventDefault();
                                            addItemToReservation();
                                        }
                                    }}
                                    placeholder="0.00 (sau negativ pentru avans)"
                                    className="mt-1"
                                />
                                {/* Min sold price indicator */}
                                {resMinSoldPrice !== null && resMinSoldPrice > 0 && (
                                    <p className="text-xs mt-1 text-amber-600 flex items-center gap-1">
                                        <span className="font-semibold">Min vândut:</span> 
                                        <span 
                                            className="font-bold cursor-pointer hover:underline"
                                            onClick={() => setReservationPrice(resMinSoldPrice.toString())}
                                            title="Click pentru a folosi acest preț"
                                        >
                                            {resMinSoldPrice.toFixed(2)} RON
                                        </span>
                                        <span className="text-zinc-400">({resTimesSold}x)</span>
                                    </p>
                                )}
                            </div>
                        </div>

                        {reservationPrice && (
                            <div className={`p-2 rounded text-center ${parseFloat(reservationPrice) < 0 ? 'bg-red-100' : 'bg-zinc-100'}`}>
                                <span className="text-sm text-zinc-600">Total: </span>
                                <span className={`font-bold ${parseFloat(reservationPrice) < 0 ? 'text-red-600' : 'text-zinc-900'}`}>
                                    {(reservationQty * parseFloat(reservationPrice || 0)).toFixed(2)} RON
                                </span>
                                {parseFloat(reservationPrice) < 0 && (
                                    <span className="text-xs text-red-500 block">Avans achitat</span>
                                )}
                            </div>
                        )}

                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setShowAddToReservationModal(false)} className="flex-1">
                                Închide
                            </Button>
                            <Button 
                                onClick={addItemToReservation} 
                                className="flex-1 bg-teal-500 hover:bg-teal-600"
                                disabled={!reservationBarcode || reservationPrice === ''}
                            >
                                <Plus size={16} className="mr-1" />
                                Adaugă
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Box Assignment Modal */}
            <Dialog open={showBoxModal} onOpenChange={setShowBoxModal}>
                <DialogContent className="max-w-sm" data-testid="box-assign-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Box size={20} className="text-blue-500" />
                            Alocă Cutie
                        </DialogTitle>
                        <DialogDescription>
                            Client: <span className="font-bold text-violet-600">{boxAssignCustomerId}</span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">Număr Cutie (L1, L2, etc.)</label>
                            <Input
                                data-testid="box-input"
                                value={boxInput}
                                onChange={(e) => setBoxInput(e.target.value.toUpperCase())}
                                placeholder="ex: L1, A2, B3"
                                className="mt-1 text-lg font-bold"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && boxInput.trim()) {
                                        assignBox();
                                    }
                                }}
                            />
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setShowBoxModal(false)} className="flex-1">
                                Anulează
                            </Button>
                            <Button 
                                data-testid="box-assign-submit"
                                onClick={assignBox}
                                className="flex-1 bg-blue-500 hover:bg-blue-600"
                                disabled={!boxInput.trim()}
                            >
                                <Box size={16} className="mr-1" />
                                Alocă
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Move Items Modal */}
            <Dialog open={showMoveModal} onOpenChange={setShowMoveModal}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ArrowRight size={20} className="text-orange-500" />
                            Mută Produse Bifate
                        </DialogTitle>
                        <DialogDescription>
                            De la: <span className="font-bold text-violet-600">{viewingReservation?.customer_id}</span>
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium">ID Client Virtual Destinație</label>
                            <Input
                                value={moveTargetCustomerId}
                                onChange={(e) => setMoveTargetCustomerId(e.target.value.toUpperCase())}
                                placeholder="ex: ABC123, XYZ999"
                                className="mt-1 text-lg font-bold"
                                autoFocus
                            />
                            <p className="text-xs text-zinc-500 mt-1">
                                Trebuie să fie un client virtual (cu minim 2 litere)
                            </p>
                        </div>
                        <div className="flex gap-3">
                            <Button variant="outline" onClick={() => setShowMoveModal(false)} className="flex-1">
                                Anulează
                            </Button>
                            <Button 
                                onClick={moveCheckedItems}
                                className="flex-1 bg-orange-500 hover:bg-orange-600"
                                disabled={!moveTargetCustomerId.trim() || moveTargetCustomerId.replace(/[^a-zA-Z]/g, '').length < 2}
                            >
                                <ArrowRight size={16} className="mr-1" />
                                Mută
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ComenziVirtuale;
